import dayjs from 'dayjs';

import type { Campaign } from '@/entities/campaign';

export type CampaignExportType = 'total' | 'post';
export type CampaignExportFormat = 'csv';

export type CampaignExportRequest = {
  type: CampaignExportType;
  format: CampaignExportFormat;
  startDate?: string;
  endDate?: string;
};

type CampaignExportAvailability = {
  disabled: boolean;
  reason?: string;
};

export type CampaignExportAvailabilityMap = Record<CampaignExportType, CampaignExportAvailability>;

export function getCampaignExportDefaultRange(campaign: Campaign) {
  const startDate = dayjs(campaign.start_date).toISOString();
  const today = dayjs().toISOString();
  const campaignEndDate = campaign.end_date ? dayjs(campaign.end_date).toISOString() : undefined;
  const endDate =
    campaignEndDate && dayjs(campaignEndDate).isBefore(today) ? campaignEndDate : today;

  return {
    startDate,
    endDate: dayjs(startDate).isAfter(endDate) ? startDate : endDate,
  };
}

export function clampCampaignExportDateRange({
  campaign,
  startDate,
  endDate,
}: {
  campaign: Campaign;
  startDate: string;
  endDate: string;
}) {
  const defaultRange = getCampaignExportDefaultRange(campaign);
  const minDate = dayjs(defaultRange.startDate);
  const maxDate = dayjs(defaultRange.endDate);
  let nextStartDate = dayjs(startDate);
  let nextEndDate = dayjs(endDate);

  if (nextStartDate.isBefore(minDate)) nextStartDate = minDate;
  if (nextStartDate.isAfter(maxDate)) nextStartDate = maxDate;
  if (nextEndDate.isBefore(minDate)) nextEndDate = minDate;
  if (nextEndDate.isAfter(maxDate)) nextEndDate = maxDate;

  if (nextStartDate.isAfter(nextEndDate)) {
    nextEndDate = nextStartDate;
  }

  if (nextEndDate.isAfter(maxDate)) {
    nextEndDate = maxDate;
  }

  if (nextStartDate.isBefore(minDate)) {
    nextStartDate = minDate;
  }

  return {
    startDate: nextStartDate.toISOString(),
    endDate: nextEndDate.toISOString(),
  };
}

export function buildCampaignExportFileName({
  campaignName,
  request,
}: {
  campaignName: string;
  request: CampaignExportRequest;
}) {
  const campaignSegment = slugifyFileSegment(campaignName);
  const exportDate = dayjs().format('YYYY-MM-DD');

  if (request.type === 'total') {
    const startDate = dayjs(request.startDate).format('YYYY-MM-DD') ?? exportDate;
    const endDate = dayjs(request.endDate).format('YYYY-MM-DD') ?? exportDate;
    return `${campaignSegment}_total-performance_${startDate}_to_${endDate}.csv`;
  }

  return `${campaignSegment}_post-performance_${exportDate}.csv`;
}

function slugifyFileSegment(value: string) {
  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalizedValue || 'campaign';
}
